import {
  forwardRef,
  type JSX,
  type ReactNode,
  type CSSProperties,
} from 'react';

import type { SvgIconSize } from './types.js';

/**
 * @public
 */
export interface SvgIconProps {
  /** Test id used for selecting the component's container. */
  'data-testid'?: string;
  /** Icon name usable for identifying the icon properly. */
  'data-iconname'?: string;
  /**
   * Size of the icon. Three predefined sizes are available, or specify a custom size in px.
   * @defaultValue 20
   */
  size?: SvgIconSize | number;
  /**
   * Classname that gets applied to the root element of the component.
   *
   * Be aware that overriding certain styles may result in unexpected behavior!
   */
  className?: string;
  /**
   * Style attribute that will be mapped to the root element of the component.
   *
   * Be aware that overriding certain styles may result in unexpected behavior!
   */
  style?: CSSProperties;
}

interface IntSvgIconProps extends SvgIconProps {
  children: ReactNode;
}

/**
 * SvgIcon component that is used to render all generated icons
 */
export const SvgIcon = /* @PURE____ */ forwardRef<
  SVGSVGElement,
  IntSvgIconProps
>(
  (
    { size, children, ...remainingProps }: IntSvgIconProps,
    forwardedRef,
  ): JSX.Element => {
    return (
      <svg
        focusable="false"
        role="img"
        aria-hidden="true"
        fill="currentColor"
        width={getIconSize(size)}
        height={getIconSize(size)}
        ref={forwardedRef}
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        {...remainingProps}
      >
        {children}
      </svg>
    );
  },
);

/** Get the icon size in pixels */
function getIconSize(size?: SvgIconSize | number): number {
  if (typeof size === 'number') {
    return size;
  }

  switch (size) {
    case 'large':
      return 32;
    case 'small':
      return 16;
    default:
      return 20;
  }
}
